import { Grid, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import { Dayjs } from 'dayjs';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { SubstitutionDataEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import { use_SetSelfWorkEventMutation } from '../../../types/planung-graphql-client-defs';
import { SubstitutionRoomsSelect } from '../components/SubstitutionRoomsSelect';
import styles from './WorkOrderForm.module.scss';

type WorkOrderFormProps = {
  originTeachers: SubstitutionDataEntry[];
  originRooms: SubstitutionDataEntry[];
  eventStart: Dayjs;
  eventEnd: Dayjs;
  eventUuid: string;
  comment: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type WorkOrderFormType = {
  roomUuids: string[];
  comment: string;
};

export const WorkOrderForm = ({
  originTeachers,
  originRooms,
  eventStart,
  eventEnd,
  eventUuid,
  comment,
  onLoad,
  onClose,
}: WorkOrderFormProps) => {
  const { t } = useTranslation();
  const eventContext = useMemorizedCacheTag('EVENT');

  const [, setSelfWork] = use_SetSelfWorkEventMutation();

  const initialValues: WorkOrderFormType = {
    roomUuids: originRooms.map((r) => r.uuid),
    comment: comment,
  };

  async function handleSubmit(values: WorkOrderFormType, formHelpers: FormikHelpers<WorkOrderFormType>) {
    onLoad(true);
    await setSelfWork(
      {
        eventUuid: eventUuid,
        substitutionComment: values.comment,
        rooms: values.roomUuids,
      },
      eventContext,
    );
    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  return (
    <Formik<WorkOrderFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors, submitForm }) => {
        return (
          <Form className={styles['work-order-form']}>
            <Grid useFormGap>
              <GridRow className={styles.info} spacingBottom='s'>
                {t('substitutions.initialTeacher')}:
                <span className={styles.bold}>
                  {originTeachers.length > 0 ? originTeachers.map((t) => t.name).join(', ') : t('common.none')}
                </span>
              </GridRow>
              <GridRow className={styles.info} spacingTop='none'>
                {t('substitutions.initialRooms')}:
                <span className={styles.bold}>
                  {originRooms.length > 0 ? originRooms.map((r) => r.name).join(', ') : t('common.none')}
                </span>
              </GridRow>
              <GridRow spacingBottom='s'>
                <SubstitutionRoomsSelect
                  originalRoomUuids={originRooms.map((r) => r.uuid)}
                  eventRoomUuids={values.roomUuids}
                  eventStart={eventStart}
                  eventEnd={eventEnd}
                  error={errors.roomUuids as string}
                  onChange={async (uuids) => await setFieldValue('roomUuids', uuids)}
                />
              </GridRow>
              <GridRow spacingTop='s'>
                <Input
                  name='comment'
                  label={t('common.comment')}
                  onChange={async (e) => setFieldValue('comment', e.target.value)}
                  value={values.comment}
                  error={errors.comment}
                />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                callback: async () => {
                  await submitForm();
                },
                disabled: isSubmitting || !dirty,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
